import { useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import Icon from "src/shared/icons"
import { FlexibleTableHeaderColumn } from "../../flexible-table-items.interface"
import "./HeaderSort.css"
import { computePosition } from "@floating-ui/react"
import { flip, shift } from "@floating-ui/dom"
import { usePortalLayer } from "src/shared/providers/PortalLayerProvider"

const HeaderSort = ({
    header,
    sortValues,
    sortFilterVAlue,
}: {
    header: FlexibleTableHeaderColumn
    sortValues: (value: "up" | "down" | null) => void
    sortFilterVAlue: {
        sort: "up" | "down" | null
        fromHeader: string
    } | null
}) => {
    const [open, setOpen] = useState(false)
    const headerRef = useRef<any>(null)
    const floatingRef = useRef<HTMLDivElement>(null)
    const portalLayer = usePortalLayer()

    // El menú se portalea al body, así que el click fuera se valida contra el header y el menú por separado
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            if (headerRef.current?.contains(target) || floatingRef.current?.contains(target)) return
            setOpen(false)
        }
        const timeout = setTimeout(() => document.addEventListener("click", handleClickOutside), 0)
        return () => {
            clearTimeout(timeout)
            document.removeEventListener("click", handleClickOutside)
        }
    }, [])

    useEffect(() => {
        if (!headerRef.current || !floatingRef.current) {
            return
        }
        computePosition(headerRef.current, floatingRef.current, {
            placement: "bottom-start",
            middleware: [flip(), shift()],
        }).then(({ x, y }) => {
            Object.assign(floatingRef!.current!.style, {
                top: `${y + 5}px`,
                left: `${x}px`,
            })
        })
    }, [open])

    const handleSelectSort = (sort: "up" | "down") => {
        setOpen(false)
        sortValues(sort)
    }

    return (
        <th
            className="flexible-table__header__cell flexible-table__cell--sort flexible-table__header__cell--pointer"
            ref={headerRef}
            onClick={(e) => {
                // El menú se portalea, pero en el árbol de React sigue siendo hijo de este <th>,
                // así que sus clicks internos llegan hasta aquí
                if (floatingRef.current?.contains(e.target as Node)) return
                setOpen((prev) => !prev)
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="flexible-table__header__cell-text" style={{ marginRight: "3px" }}>
                    {header.valueToDisplay || header.value}
                </span>
                <Icon name={"sort"} color="var(--purple-drawer-primario)" />
            </div>
            {open &&
                ReactDOM.createPortal(
                    <div className="flexible-table__cell--sort__menu" ref={floatingRef}>
                        <div
                            onClick={(e) => {
                                e.stopPropagation()
                                handleSelectSort("down")
                            }}
                            className={`flexible-table__cell--sort__menu__item ${
                                sortFilterVAlue?.fromHeader === header.value && sortFilterVAlue.sort === "down"
                                    ? "flexible-table__cell--sort__menu__item--active"
                                    : ""
                            }`}
                        >
                            <span className="flexible-table__cell--sort__menu__text">De mayor a menor</span>
                        </div>
                        <div
                            onClick={(e) => {
                                e.stopPropagation()
                                handleSelectSort("up")
                            }}
                            className={`flexible-table__cell--sort__menu__item ${
                                sortFilterVAlue?.fromHeader === header.value && sortFilterVAlue.sort === "up"
                                    ? "flexible-table__cell--sort__menu__item--active"
                                    : ""
                            }`}
                        >
                            <span className="flexible-table__cell--sort__menu__text">De menor a mayor</span>
                        </div>
                    </div>,
                    portalLayer
                )}
        </th>
    )
}

export default HeaderSort
